import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { runCommentSchema, type RunCommentDto } from '@rw/shared';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { SectionTitle, Avatar, Button } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { MY_COLOR } from '../../lib/colors';
import { timeAgo } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';

export const commentKeys = { run: (runId: string) => ['run-comments', runId] as const };
const KUDOS = '🏃‍♂️';

/** The comment being answered. */
interface ReplyTarget {
  id: string;
  name: string;
}

/** Strava-style comment thread under a finished run: replies one level deep, kudos on comments. */
export function RunComments({ runId }: { runId: string }) {
  const me = useAuth((s) => s.user?.id);
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const t = useT();
  // Guards double posts (IME Enter + real Enter, double clicks) before React re-renders.
  const sending = useRef(false);
  const key = commentKeys.run(runId);
  const q = useQuery({ queryKey: key, queryFn: () => api<RunCommentDto[]>(`/runs/${runId}/comments`), refetchInterval: 60_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['runs', runId] });
  };
  const send = useMutation({
    mutationFn: (v: { body: string; replyTo?: string }) => api<RunCommentDto>(`/runs/${runId}/comments`, { body: v }),
    onSuccess: () => {
      setText('');
      setReplyTo(null);
      refresh();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Could not post the comment') }),
    onSettled: () => {
      sending.current = false;
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/runs/${runId}/comments/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Could not delete') }),
  });
  const kudos = useMutation({
    mutationFn: (v: { id: string; give: boolean }) => api<RunCommentDto>(`/runs/${runId}/comments/${v.id}/kudos`, { method: v.give ? 'POST' : 'DELETE' }),
    onMutate: ({ id, give }) => {
      const prev = qc.getQueryData<RunCommentDto[]>(key);
      if (prev) {
        qc.setQueryData<RunCommentDto[]>(
          key,
          prev.map((c) => (c.id === id ? { ...c, kudoedByMe: give, kudosCount: c.kudosCount + (give ? 1 : -1) } : c)),
        );
      }
      return { prev };
    },
    onSuccess: (c) => qc.setQueryData<RunCommentDto[]>(key, (cur) => cur?.map((x) => (x.id === c.id ? c : x))),
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Could not give kudos') });
    },
  });
  const submit = () => {
    if (sending.current) return;
    const parsed = runCommentSchema.safeParse({ body: text, ...(replyTo ? { replyTo: replyTo.id } : {}) });
    if (!parsed.success) return toast({ kind: 'error', title: t(parsed.error.issues[0]?.message ?? 'Invalid comment') });
    sending.current = true;
    send.mutate(parsed.data);
  };
  const startReply = (c: RunCommentDto) => {
    setReplyTo({ id: c.id, name: c.user.displayName });
    input.current?.focus();
  };

  const items = q.data ?? [];
  const top = items.filter((c) => !c.parentId);
  const repliesOf = (id: string) => items.filter((c) => c.parentId === id);
  const row = (c: RunCommentDto) => (
    <Comment
      key={c.id}
      c={c}
      me={me}
      onReply={() => startReply(c)}
      onKudos={() => kudos.mutate({ id: c.id, give: !c.kudoedByMe })}
      onDelete={() => remove.mutate(c.id)}
    />
  );

  return (
    <div data-testid="run-comments">
      <SectionTitle>
        {t('Comments')}
        {items.length ? ` · ${items.length}` : ''}
      </SectionTitle>
      {top.length ? (
        <ul className="space-y-3">
          {top.map((c) => {
            const replies = repliesOf(c.id);
            return (
              <li key={c.id}>
                {row(c)}
                {replies.length ? (
                  <ul className="ml-11 mt-2 space-y-2 border-l border-ink-700 pl-3" data-testid="comment-replies">
                    {replies.map((r) => (
                      <li key={r.id}>{row(r)}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : q.isPending ? null : (
        <p className="text-sm text-ink-500">{t('No comments yet — say something nice.')}</p>
      )}
      {replyTo ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-ink-400" data-testid="replying-to">
          <span className="min-w-0 truncate">{t('Replying to {name}', { name: replyTo.name })}</span>
          <button className="shrink-0 font-semibold text-ink-300 hover:text-ink-100" onClick={() => setReplyTo(null)} aria-label={t('Cancel reply')}>
            ✕
          </button>
        </div>
      ) : null}
      <div className={`${replyTo ? 'mt-1' : 'mt-3'} flex items-end gap-2`}>
        <textarea
          ref={input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Vietnamese/CJK input methods use Enter to commit the word being composed — not a submit.
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
            if (e.key === 'Escape' && replyTo) setReplyTo(null);
          }}
          maxLength={500}
          rows={1}
          placeholder={replyTo ? t('Write a reply…') : t('Add a comment…')}
          className="max-h-32 min-h-11 flex-1 resize-none rounded-xl border border-ink-700 bg-ink-900 px-3 py-2.5 text-sm outline-none focus:border-volt"
          data-testid="comment-input"
        />
        <Button size="sm" className="h-11" onClick={submit} loading={send.isPending} disabled={!text.trim()} data-testid="comment-send">
          {t(replyTo ? 'Reply' : 'Post')}
        </Button>
      </div>
    </div>
  );
}

function Comment({
  c,
  me,
  onReply,
  onKudos,
  onDelete,
}: {
  c: RunCommentDto;
  me: string | undefined;
  onReply: () => void;
  onKudos: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const mine = c.user.id === me;
  return (
    <div className="flex gap-3" data-testid="run-comment">
      <Link to={mine ? '/profile' : `/players/${c.user.id}`} className="shrink-0">
        <Avatar name={c.user.displayName} color={mine ? MY_COLOR : c.user.color} src={c.user.avatarUrl} size="sm" />
      </Link>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl rounded-tl-sm bg-ink-850 px-3 py-2">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold">{c.user.displayName}</span>
            <span className="shrink-0 text-[11px] text-ink-500">{timeAgo(c.createdAt)}</span>
          </div>
          <p className="whitespace-pre-line break-words text-sm text-ink-200">{c.body}</p>
        </div>
        <div className="mt-1 flex items-center gap-4 px-1 text-xs font-semibold text-ink-400">
          {mine ? (
            c.kudosCount ? (
              <span className="flex items-center gap-1" data-testid="comment-kudos-count">
                <span className="text-sm leading-none">{KUDOS}</span>
                {c.kudosCount}
              </span>
            ) : null
          ) : (
            <button
              onClick={onKudos}
              aria-pressed={c.kudoedByMe}
              aria-label={t(c.kudoedByMe ? 'Kudos given' : 'Give kudos')}
              className={`flex items-center gap-1 transition active:scale-95 ${c.kudoedByMe ? 'text-volt' : 'hover:text-ink-200'}`}
              data-testid="comment-kudos"
            >
              <span className={`text-sm leading-none ${c.kudoedByMe ? '' : 'grayscale'}`}>{KUDOS}</span>
              {c.kudosCount ? c.kudosCount : t('Kudos')}
            </button>
          )}
          <button onClick={onReply} className="hover:text-ink-200" data-testid="comment-reply">
            {t('Reply')}
          </button>
          {c.canDelete ? (
            <button className="ml-auto hover:text-enemy" onClick={onDelete} aria-label={t('Delete comment')}>
              {t('Delete')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
